/* istanbul ignore file */
import { useDispatch } from 'react-redux';
import { queryClient } from 'AppWrapper';
import { updateStudentUnitSelection } from 'state/slices/studentUnitSelectionSlice';
import {
  resetUnitSelection,
  updateUnitSelection,
  updateWaitlistSelections,
} from 'state/slices/unitSelectionSlice';

import { useLocale } from 'hooks/utils/useLocale';
import { getAvailableUnitsAndLeaseTermStudent, getFloorPlans } from 'services';
import type { FloorPlan } from 'types/floorplan-page-types';

import useGetAvailableUnitAndLeaseTerm from './useGetAvailableUnitsAndLeaseTerms';

export function useSetFloorPlanPageDataInStore() {
  const dispatch = useDispatch();
  const locale = useLocale();

  const { fetchAvailableUnitsAndLeaseTerm } = useGetAvailableUnitAndLeaseTerm(locale);

  async function fetchFloorPlans(
    propertyId: number,
    floorPlanId: number,
    moveInDate: string,
    leaseTermId?: string,
    leaseStartWindowId?: string,
    spaceConfigurationId?: string,
  ) {
    try {
      const isStudentLaunch =
        spaceConfigurationId != null || (leaseTermId != null && leaseStartWindowId != null);
      const studentParams = isStudentLaunch
        ? {
            leaseTermIds: leaseTermId ? [Number(leaseTermId)] : undefined,
            leaseStartWindowIds: leaseStartWindowId ? [Number(leaseStartWindowId)] : undefined,
            spaceConfigurationIds: spaceConfigurationId
              ? [Number(spaceConfigurationId)]
              : undefined,
          }
        : undefined;
      const data = await queryClient.fetchQuery({
        queryKey: [
          'floor-plans',
          propertyId,
          moveInDate,
          leaseTermId,
          leaseStartWindowId,
          spaceConfigurationId,
          locale,
        ],
        queryFn: () => getFloorPlans(propertyId, moveInDate, undefined, studentParams),
      });

      const selectedFloorPlan = data.find((i: FloorPlan) => Number(i.id) === Number(floorPlanId));
      if (
        selectedFloorPlan ||
        (leaseTermId == null && leaseStartWindowId == null && spaceConfigurationId == null)
      ) {
        return selectedFloorPlan;
      }

      // Core Config can omit a launch-selected floorplan when it cannot apply
      // the optional student filters (for example, a launch has a space option
      // but no lease term). Retry without them before clearing the selection.
      const unfilteredData = await queryClient.fetchQuery({
        queryKey: ['floor-plans', propertyId, moveInDate, 'launch-restore-fallback', locale],
        queryFn: () => getFloorPlans(propertyId, moveInDate),
      });
      return unfilteredData.find((i: FloorPlan) => Number(i.id) === Number(floorPlanId));
    } catch {
      throw new Error('Error while fetching floor plans.');
    }
  }

  async function initializeFloorPlanPageData(
    moveInDate: string,
    floorPlanId: number,
    unitSpaceId: number,
    termMonth: string,
    propertyId: number,
    leaseId: number,
    waitlistSelections: any,
    spaceConfigurationId?: string,
    leaseTermId?: string,
    leaseStartWindowId?: string,
    preferredAmenityIds?: number[],
    isStudentLaunch = false,
  ) {
    try {
      if (
        (!floorPlanId || !moveInDate) &&
        (!waitlistSelections || waitlistSelections.floorplanIds.length === 0)
      )
        throw new Error('Missing floor plan or move in date and no waitlist selections.');

      let hasError = true;

      // A student launch can intentionally carry only a lease term/window. Do not
      // restore its floor plan as a completed bulk-assignment selection: the
      // applicant must still select a floor plan and space configuration. The
      // explicit marker distinguishes this persisted state from a conventional
      // launch that happens to have lease-term data.
      if (isStudentLaunch && !spaceConfigurationId && leaseTermId) {
        dispatch(updateUnitSelection({ moveInDate }));
        dispatch(
          updateStudentUnitSelection({
            selectedLeaseTermId: leaseTermId,
            selectedLeaseStartWindowId: leaseStartWindowId || '',
          }),
        );
        return 1;
      }

      if (floorPlanId) {
        const floorPlan = await fetchFloorPlans(
          propertyId,
          floorPlanId,
          moveInDate,
          leaseTermId,
          leaseStartWindowId,
          spaceConfigurationId,
        );
        if (!floorPlan) throw new Error('Selected floor plan is not available.');

        const availablePreferredAmenityIds =
          floorPlan?.floorplanPreferredAmenities?.map((item) => item.id) ?? [];
        const filteredPreferredAmenityIds = (preferredAmenityIds ?? []).filter((id) =>
          availablePreferredAmenityIds.includes(id),
        );

        // Check if this is student occupancy (has spaceConfigurationId)
        const isStudentOccupancy = !!spaceConfigurationId;
        // Check if this is bulk assignment mode (no unitSpaceId but has spaceConfigurationId)
        const isBulkAssignmentMode = isStudentOccupancy && !unitSpaceId;

        let unit: any;
        let leaseTerm: any;

        if (isBulkAssignmentMode) {
          // Bulk assignment mode: no unit selection, just floorPlan + spaceOption + leaseTerm
          // Don't fetch units - we don't need them in bulk mode

          // Create a lease term object for the DateBasedLeaseTermSelector
          if (leaseTermId) {
            leaseTerm = {
              id: Number(leaseTermId),
              name: '', // Will be populated when DateBasedLeaseTermSelector loads
              amount: 0, // Will be populated when DateBasedLeaseTermSelector loads
              rateFrequency: 'monthly', // Default value
              termMonth,
              deposit: 0, // Will be populated when DateBasedLeaseTermSelector loads
              leaseStartWindowId: leaseStartWindowId || '',
            };
          }

          dispatch(
            updateUnitSelection({
              floorPlan,
              unit: null, // No unit in bulk assignment mode
              leaseTerm,
              moveInDate,
            }),
          );

          // Restore student-specific data
          const studentData: any = {
            availableSpaceConfigurationIds: [Number(spaceConfigurationId)],
            selectedPreferredAmenityIds: filteredPreferredAmenityIds,
          };

          if (leaseTermId) {
            studentData.selectedLeaseTermId = leaseTermId;
            studentData.selectedLeaseStartWindowId = leaseStartWindowId || '';
          }

          dispatch(updateStudentUnitSelection(studentData));

          hasError = false;
        } else if (isStudentOccupancy) {
          // Student unit selection mode: need to fetch and restore unit
          const data = await queryClient.fetchQuery({
            queryKey: [
              'units-student',
              floorPlanId,
              moveInDate,
              leaseId,
              [],
              leaseTermId,
              leaseStartWindowId,
              locale,
            ],
            queryFn: () =>
              getAvailableUnitsAndLeaseTermStudent(
                propertyId,
                floorPlanId,
                moveInDate,
                leaseId,
                false,
                spaceConfigurationId ? [Number(spaceConfigurationId)] : [],
                leaseTermId ? Number(leaseTermId) : undefined,
                leaseStartWindowId ? Number(leaseStartWindowId) : undefined,
              ),
          });

          // For student occupancy, match the selected nested unit space.
          unit = data.find((u: any) =>
            u.unitSpaces?.some(
              (unitSpace: any) => String(unitSpace.unitSpaceId) === String(unitSpaceId),
            ),
          );
          if (!unit) throw new Error('Unit is not available.');

          // Create a lease term object since it's not in the API response
          if (leaseTermId) {
            leaseTerm = {
              id: Number(leaseTermId),
              name: '',
              amount: 0,
              rateFrequency: 'monthly',
              termMonth,
              deposit: 0,
              leaseStartWindowId: leaseStartWindowId || '',
            };
          }

          dispatch(
            updateUnitSelection({
              floorPlan,
              unit,
              leaseTerm,
              moveInDate,
            }),
          );

          // Restore student-specific data
          const studentData: any = {
            selectedSpace: {
              unitSpaceId: unitSpaceId.toString(),
              spaceConfigurationId,
            },
            availableSpaceConfigurationIds: [Number(spaceConfigurationId)],
            selectedPreferredAmenityIds: filteredPreferredAmenityIds,
          };

          if (leaseTermId) {
            studentData.selectedLeaseTermId = leaseTermId;
            studentData.selectedLeaseStartWindowId = leaseStartWindowId || '';
          }

          dispatch(updateStudentUnitSelection(studentData));

          hasError = false;
        } else if (unitSpaceId) {
          // Use regular API for non-student occupancy
          const result = await fetchAvailableUnitsAndLeaseTerm(
            propertyId,
            floorPlanId,
            moveInDate,
            unitSpaceId,
            termMonth,
            leaseId,
            leaseTermId,
          );
          unit = result.unit;
          leaseTerm = result.leaseTerm;

          dispatch(
            updateUnitSelection({
              floorPlan,
              unit,
              leaseTerm,
              moveInDate,
            }),
          );

          hasError = false;
        } else {
          // A conventional launch can intentionally select only a floor plan.
          // Preserve that selection and let the applicant choose a unit and lease term.
          dispatch(
            updateUnitSelection({
              floorPlan,
              unit: null,
              leaseTerm: null,
              moveInDate,
            }),
          );

          hasError = false;
        }
      }
      if (waitlistSelections && waitlistSelections.floorplanIds.length > 0) {
        dispatch(updateWaitlistSelections({ waitlistSelections }));
        hasError = false;
      }

      return hasError ? 1 : 0; // Returning error count for floor plan page
    } catch {
      dispatch(resetUnitSelection());
      return 1; // Returning error count for floor plan page
    }
  }

  return initializeFloorPlanPageData;
}
